import { useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function Roles() {
  const roles = useFetch("/admin/roles", []);
  const perms = useFetch("/admin/permissions", []);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const allPerms = perms.data?.permissions || [];
  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const create = async () => {
    if (!name.trim()) return toast.error("Role name required");
    setBusy(true);
    try { await api.post("/admin/roles", { name: name.trim(), permissions: selected }); toast.success("Role created"); setOpen(false); setName(""); setSelected([]); roles.refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const savePerms = async (role, next) => {
    try { await api.put(`/admin/roles/${role.id}`, { name: role.name, permissions: next }); toast.success("Permissions updated"); roles.refetch(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Roles & Permissions" subtitle="Manage admin roles and their permissions" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-role-btn"><Plus className="h-4 w-4 mr-1.5" /> New Role</Button></DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="role-dialog">
            <DialogHeader><DialogTitle>New Admin Role</DialogTitle></DialogHeader>
            <div><Label>Role name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. catalog_manager" className="mt-1.5" data-testid="role-name-input" /></div>
            <div>
              <Label>Permissions</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                {allPerms.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-muted" data-testid={`perm-opt-${p}`}>
                    <input type="checkbox" checked={selected.includes(p)} onChange={() => toggle(p)} className="h-3.5 w-3.5 accent-[hsl(var(--primary))]" />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={busy} data-testid="role-save-btn">{busy ? "Saving…" : "Create role"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      {roles.loading || perms.loading ? <Loading /> : roles.error ? <ErrorState error={roles.error} onRetry={roles.refetch} /> :
        !roles.data?.length ? <SectionCard className="p-2"><EmptyState label="No roles" /></SectionCard> : (
          <div className="space-y-4" data-testid="roles-list">
            {roles.data.map((role) => {
              const isSuper = role.permissions?.includes("*");
              return (
                <SectionCard key={role.id} className="p-5" data-testid={`role-${role.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    <span className="font-semibold font-outfit">{role.name}</span>
                    {isSuper && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">ALL ACCESS</span>}
                  </div>
                  {isSuper ? <p className="text-sm text-muted-foreground">Full access to every permission.</p> : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {allPerms.map((p) => {
                        const on = role.permissions?.includes(p);
                        return (
                          <div key={p} className="flex items-center justify-between gap-2 text-xs border rounded-lg px-3 py-2">
                            <span className="truncate">{p}</span>
                            <Switch checked={on} onCheckedChange={(v) => savePerms(role, v ? [...(role.permissions || []), p] : role.permissions.filter((x) => x !== p))} data-testid={`role-${role.id}-perm-${p}`} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </SectionCard>
              );
            })}
          </div>
        )}
    </div>
  );
}
